import { useTranslation } from 'react-i18next';

import { Button } from '@app/shared/ui/atoms/Common';
import { PredictionCookieReveal } from '@app/shared/ui/molecules/Common';
import { Modal, type ModalProps } from '@app/shared/ui/organisms/Common';
import { useBalance } from '@app/shared/hooks/useBalance';
import { useFeedPandaMutation } from '@app/store/slices/userProgress/userProgressApi';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { telegramVibrate, telegramNotify } from '@app/shared/utils/common';
import notification from '@app/shared/utils/notification';
import { DAILY_COOKIES_FEED_COUNT, FEED_COOLDOWN_HOURS } from '@app/shared/constants/common';
import { PriceType, HttpStatusCode } from '@app/shared/types/common';
import { eatingPandaImage, pandaFaceImage, cookieImage } from '@app/assets';

interface FeedPandaModalProps extends Pick<ModalProps, 'isOpen' | 'onOpenChange'> {
  onFeedSuccess: () => void;
  isTimerExpired: boolean;
}

export function FeedPandaModal({
  isOpen,
  onOpenChange,
  onFeedSuccess,
  isTimerExpired,
}: FeedPandaModalProps) {
  const { t } = useTranslation();
  const { isBalanceEnough } = useBalance();
  const { handleFetchError } = useApiErrorHandler();
  const [feedPanda, { data: prediction, isLoading: isPredictionLoading, reset: resetPrediction }] =
    useFeedPandaMutation();

  const handleNotEnoughBalance = () => {
    telegramNotify('error');
    notification.error(t('errors.balance'));
  };

  const handleFeedSuccess = () => {
    onFeedSuccess();

    const pandaPhrases = t('panda_phrases', { returnObjects: true }) as string[];
    const randomPhrase = pandaPhrases[Math.floor(Math.random() * (pandaPhrases.length - 1))];
    notification.success(randomPhrase, <img src={pandaFaceImage} alt="panda" className="w-5" />);
  };

  const handleFeedError = (errorMessage: string) => {
    telegramNotify('error');
    notification.error(errorMessage);
  };

  const handleFeedClick = async () => {
    telegramVibrate();

    if (!isTimerExpired) {
      handleFeedError(t('home.feed_locked', { cooldown: FEED_COOLDOWN_HOURS }));
      return;
    }

    if (!isBalanceEnough(PriceType.COOKIE, DAILY_COOKIES_FEED_COUNT)) {
      handleNotEnoughBalance();
      return;
    }

    try {
      await feedPanda().unwrap();
      handleFeedSuccess();
    } catch (e: unknown) {
      if (
        typeof e === 'object' &&
        e &&
        'status' in e &&
        e.status === HttpStatusCode.TOO_MANY_REQUESTS
      ) {
        handleFeedError(t('home.feed_locked', { cooldown: FEED_COOLDOWN_HOURS }));
        return;
      }

      handleFetchError(e);
    }
  };

  const handleModalClose = () => {
    onOpenChange?.(false);
    resetPrediction();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleModalClose}
      featuredImg={<img src={eatingPandaImage} alt="eating panda" className="w-[134px]" />}
      hiddenTitle="Feed panda"
    >
      <PredictionCookieReveal cookie={prediction?.cookie ?? { text: null, image: null }} />

      {!prediction?.cookie && (
        <Button
          onClick={handleFeedClick}
          disabled={isPredictionLoading || !!prediction}
          className="flex justify-center items-center gap-1"
        >
          {isPredictionLoading ? (
            t('common.wait')
          ) : (
            <>
              {t('home.feed')} {DAILY_COOKIES_FEED_COUNT}
              <img src={cookieImage} className="h-4" alt="cookie image" />
            </>
          )}
        </Button>
      )}
    </Modal>
  );
}
